import React, { useState, type ImgHTMLAttributes } from "react";
import { ImageOff } from "lucide-react";

const RESPONSIVE_WIDTHS = [64, 96, 160, 240, 320, 480, 768, 1024, 1600] as const;

export function optimizedSource(src: string, width: number) {
  const filename = src.split("/").pop() ?? src;
  const baseName = filename.replace(/\.[^.]+$/, "");
  return `/optimized-media/${baseName}-${width}.webp`;
}

function sourceSet(src: string, widths: readonly number[]) {
  return widths
    .map(width => `${optimizedSource(src, width)} ${width}w`)
    .join(", ");
}

type ResponsiveStorageImageProps = Omit<
  ImgHTMLAttributes<HTMLImageElement>,
  "src" | "srcSet" | "width" | "height"
> & {
  source: string;
  width: number;
  height: number;
  sizes: string;
  fallbackWidth?: (typeof RESPONSIVE_WIDTHS)[number];
  responsiveWidths?: readonly (typeof RESPONSIVE_WIDTHS)[number][];
};

export function ResponsiveStorageImage({
  source,
  width,
  height,
  sizes,
  fallbackWidth = 1024,
  responsiveWidths = RESPONSIVE_WIDTHS,
  ...imageProps
}: ResponsiveStorageImageProps) {
  const [loadFailed, setLoadFailed] = useState(false);

  // Last-resort visual fallback when even the optimized source fails to
  // load (e.g. the image optimizer proxy is unreachable) — without this,
  // callers rendered bare broken alt text with no visible box (see GH-212).
  if (loadFailed) {
    // An empty alt marks the source image decorative -- role="img" with an
    // empty (but present) aria-label is an axe role-img-alt violation, and
    // a screen reader should skip this box the same way it would skip the
    // <img alt=""> it's replacing.
    const isDecorative = imageProps.alt === "";
    return (
      <div
        data-testid="responsive-storage-image-fallback"
        className={[imageProps.className, "flex items-center justify-center bg-[var(--tfrs-navy-panel)] border border-white/10 text-white/40"]
          .filter(Boolean)
          .join(" ")}
        style={{ width, height }}
        {...(isDecorative
          ? { "aria-hidden": "true" as const }
          : { role: "img", "aria-label": typeof imageProps.alt === "string" ? imageProps.alt : undefined })}
      >
        <ImageOff className="h-1/2 w-1/2 max-h-8 max-w-8" aria-hidden="true" />
      </div>
    );
  }

  return (
    <picture>
      <source srcSet={sourceSet(source, responsiveWidths)} sizes={sizes} type="image/webp" />
      <img
        {...imageProps}
        src={optimizedSource(source, fallbackWidth)}
        width={width}
        height={height}
        onError={(event) => {
          imageProps.onError?.(event);
          setLoadFailed(true);
        }}
      />
    </picture>
  );
}
